// ESPP2-5062
export type ZeroQueryOverwriteFeature = "enabled" | "disabled";
type AbTestModeListener = (abMode: ZeroQueryOverwriteFeature) => void;

const AB_TEST_ORIGINAL_CLASS = "ab-zero-query-original";
const AB_TEST_VARIANT_CLASS = "ab-zero-query-variant";
const DEFAULT_ZERO_QUERY_FEATURE: ZeroQueryOverwriteFeature = "enabled";

function mapAbTestMode(classList: DOMTokenList | null): ZeroQueryOverwriteFeature {
    if (classList == null) return DEFAULT_ZERO_QUERY_FEATURE;
    if (classList.contains(AB_TEST_ORIGINAL_CLASS)) {
        return "disabled";
    } else if (classList.contains(AB_TEST_VARIANT_CLASS)) {
        return "enabled";
    } else return DEFAULT_ZERO_QUERY_FEATURE;
}

const listeners = new Set<AbTestModeListener>();
let observer: MutationObserver | null = null;

function notify(abTestMode: ZeroQueryOverwriteFeature): void {
    listeners.forEach((fn) => fn(abTestMode));
}

function startObserver(): void {
    const htmlElement = document.documentElement;

    observer = new MutationObserver((mutations: MutationRecord[]) => {
        for (const mutation of mutations) {
            if (mutation.type === "attributes" && mutation.attributeName === "class") {
                notify(mapAbTestMode((mutation.target as HTMLElement).classList));
                break;
            }
        }
    });

    observer.observe(htmlElement, {
        attributes: true,
        attributeFilter: ["class"]
    });
}

function stopObserver(): void {
    observer?.disconnect();
    observer = null;
}

export function getCurrentZeroQueryOverwrite(): ZeroQueryOverwriteFeature {
    return mapAbTestMode(document.documentElement?.classList ?? null);
}

/**
 * A subscription on the `html` class list that provides notifications
 * when the AB-Test mode changes to `ab-zero-query-original` or `ab-zero-query-variant`.
 */
export function subscribeToAbTestMode(listener: AbTestModeListener): () => void {
    const isFirst = listeners.size === 0;
    listeners.add(listener);

    if (isFirst) {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", startObserver, { once: true });
        } else {
            startObserver();
        }
    }

    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            stopObserver();
        }
    };
}
